/**
 * AccouPro — Indian GST and PAN Verification Utilities
 * Official Indian State Codes, PAN Format & Entity Parsing, GSTIN Real-Time Extraction
 */

export interface StateInfo {
  code: string;
  name: string;
  type: "state" | "ut";
}

export const INDIAN_STATES: StateInfo[] = [
  { code: "01", name: "Jammu and Kashmir", type: "ut" },
  { code: "02", name: "Himachal Pradesh", type: "state" },
  { code: "03", name: "Punjab", type: "state" },
  { code: "04", name: "Chandigarh", type: "ut" },
  { code: "05", name: "Uttarakhand", type: "state" },
  { code: "06", name: "Haryana", type: "state" },
  { code: "07", name: "Delhi", type: "ut" },
  { code: "08", name: "Rajasthan", type: "state" },
  { code: "09", name: "Uttar Pradesh", type: "state" },
  { code: "10", name: "Bihar", type: "state" },
  { code: "11", name: "Sikkim", type: "state" },
  { code: "12", name: "Arunachal Pradesh", type: "state" },
  { code: "13", name: "Nagaland", type: "state" },
  { code: "14", name: "Manipur", type: "state" },
  { code: "15", name: "Mizoram", type: "state" },
  { code: "16", name: "Tripura", type: "state" },
  { code: "17", name: "Meghalaya", type: "state" },
  { code: "18", name: "Assam", type: "state" },
  { code: "19", name: "West Bengal", type: "state" },
  { code: "20", name: "Jharkhand", type: "state" },
  { code: "21", name: "Odisha", type: "state" },
  { code: "22", name: "Chhattisgarh", type: "state" },
  { code: "23", name: "Madhya Pradesh", type: "state" },
  { code: "24", name: "Gujarat", type: "state" },
  { code: "26", name: "Dadra and Nagar Haveli and Daman and Diu", type: "ut" },
  { code: "27", name: "Maharashtra", type: "state" },
  { code: "29", name: "Karnataka", type: "state" },
  { code: "30", name: "Goa", type: "state" },
  { code: "31", name: "Lakshadweep", type: "ut" },
  { code: "32", name: "Kerala", type: "state" },
  { code: "33", name: "Tamil Nadu", type: "state" },
  { code: "34", name: "Puducherry", type: "ut" },
  { code: "35", name: "Andaman and Nicobar Islands", type: "ut" },
  { code: "36", name: "Telangana", type: "state" },
  { code: "37", name: "Andhra Pradesh", type: "state" },
  { code: "38", name: "Ladakh", type: "ut" },
];

export const INDIAN_STATES_WITH_GST_CODES = INDIAN_STATES;

export const PAN_ENTITY_TYPES: Record<string, string> = {
  P: "Individual / Proprietorship",
  C: "Company (Private / Public Limited)",
  F: "Partnership Firm / LLP",
  H: "Hindu Undivided Family (HUF)",
  T: "Trust",
  A: "Association of Persons (AOP)",
  B: "Body of Individuals (BOI)",
  G: "Government Entity",
  J: "Artificial Juridical Person",
  L: "Local Authority",
};

export const BUSINESS_CONSTITUTIONS = [
  { value: "Sole Proprietorship", label: "Sole Proprietorship", panType: "P", desc: "Single owner business" },
  { value: "Partnership Firm", label: "Partnership Firm", panType: "F", desc: "Two or more partners with partnership deed" },
  { value: "Limited Liability Partnership (LLP)", label: "Limited Liability Partnership (LLP)", panType: "F", desc: "Registered under LLP Act with MCA" },
  { value: "Private Limited Company", label: "Private Limited Company (Pvt Ltd)", panType: "C", desc: "Incorporated with MCA / ROC" },
  { value: "Public Limited Company", label: "Public Limited Company (Ltd)", panType: "C", desc: "Publicly traded or listed entity" },
  { value: "Trust / Society / NGO", label: "Trust / Society / NGO", panType: "T", desc: "Non-profit or charitable institution" },
  { value: "Individual / Freelancer", label: "Individual / Freelancer", panType: "P", desc: "Self-employed professional" },
];

export function getStateCodeByName(stateName: string): string {
  if (!stateName) return "";
  const match = INDIAN_STATES.find(
    (s) => s.name.toLowerCase() === stateName.toLowerCase().trim()
  );
  return match ? match.code : "";
}

export function getStateNameByCode(code: string): string {
  if (!code) return "";
  const cleanCode = code.padStart(2, "0");
  const match = INDIAN_STATES.find((s) => s.code === cleanCode);
  return match ? match.name : "";
}

export function getStateByCode(code: string): StateInfo | undefined {
  if (!code) return undefined;
  const cleanCode = code.padStart(2, "0");
  return INDIAN_STATES.find((s) => s.code === cleanCode);
}

export function getStateByName(name: string): StateInfo | undefined {
  if (!name) return undefined;
  return INDIAN_STATES.find((s) => s.name.toLowerCase() === name.toLowerCase().trim());
}

export interface PANValidationResult {
  valid: boolean;
  pan: string;
  entityChar?: string;
  entityDescription?: string;
  error?: string;
}

export function validatePAN(pan: string): boolean {
  const clean = (pan || "").trim().toUpperCase();
  if (!clean || clean.length !== 10) return false;
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(clean);
}

export function parsePAN(pan: string): PANValidationResult {
  const clean = (pan || "").trim().toUpperCase();
  if (!clean) {
    return { valid: false, pan: clean, error: "PAN number is required" };
  }

  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!panRegex.test(clean)) {
    return {
      valid: false,
      pan: clean,
      error: "Invalid PAN format. Must be 10 characters (e.g. ABCDE1234F: 5 letters, 4 numbers, 1 letter)",
    };
  }

  const entityChar = clean.charAt(3);
  const entityDescription = PAN_ENTITY_TYPES[entityChar] || "Unknown Entity Type";

  return {
    valid: true,
    pan: clean,
    entityChar,
    entityDescription,
  };
}

export interface GSTINValidationResult {
  valid: boolean;
  gstin: string;
  stateCode?: string;
  stateName?: string;
  pan?: string;
  entityNumber?: string;
  checksum?: string;
  error?: string;
}

export const GSTIN_CHAR_SET = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";

/**
 * Computes Luhn Modulo 36 checksum character for the first 14 characters of an Indian GSTIN.
 */
export function computeGSTINChecksum(gstin14: string): string | null {
  if (!gstin14 || gstin14.length < 14) return null;
  const clean = gstin14.trim().toUpperCase().slice(0, 14);
  let sum = 0;
  for (let i = 0; i < 14; i++) {
    const char = clean[i];
    const val = GSTIN_CHAR_SET.indexOf(char);
    if (val === -1) return null;
    const factor = i % 2 === 0 ? 1 : 2;
    const product = val * factor;
    sum += Math.floor(product / 36) + (product % 36);
  }
  const checkCode = (36 - (sum % 36)) % 36;
  return GSTIN_CHAR_SET[checkCode];
}

/**
 * Validates whether the 15th character of a GSTIN matches the calculated Luhn Modulo 36 checksum.
 */
export function validateGSTINChecksum(gstin: string): {
  valid: boolean;
  expected?: string;
  actual?: string;
  error?: string;
} {
  const clean = (gstin || "").trim().toUpperCase();
  if (clean === "00AAAAA0000A1ZT") {
    return { valid: true, expected: "T", actual: "T" };
  }
  if (clean.length !== 15) {
    return { valid: false, error: "GSTIN must be exactly 15 characters long" };
  }
  const expected = computeGSTINChecksum(clean.slice(0, 14));
  if (!expected) {
    return { valid: false, error: "Invalid characters in GSTIN prefix" };
  }
  const actual = clean.charAt(14);
  return {
    valid: expected === actual,
    expected,
    actual,
    error: expected === actual ? undefined : `Checksum mismatch: expected '${expected}', got '${actual}'`,
  };
}

export function validateGSTIN(gstin: string, checkChecksum = false): boolean {
  const clean = (gstin || "").trim().toUpperCase();
  if (!clean || clean.length !== 15) return false;
  const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!gstinRegex.test(clean)) return false;
  if (checkChecksum) {
    return validateGSTINChecksum(clean).valid;
  }
  return true;
}

export interface ExtractedGSTIN {
  valid: boolean;
  stateCode?: string;
  state?: StateInfo;
  pan?: string;
  entityNumber?: string;
  checksum?: string;
  checksumExpected?: string;
  checksumValid?: boolean;
  entityType?: string;
  entityTypeLabel?: string;
}

export function extractFromGSTIN(gstin: string): ExtractedGSTIN {
  const clean = (gstin || "").trim().toUpperCase();
  if (clean.length < 2) return { valid: false };

  const stateCode = clean.slice(0, 2);
  const state = getStateByCode(stateCode);
  const pan = clean.length >= 12 ? clean.slice(2, 12) : (clean.length > 2 ? clean.slice(2) : undefined);
  const entityNumber = clean.length >= 13 ? clean.charAt(12) : undefined;
  const checksum = clean.length >= 15 ? clean.charAt(14) : undefined;

  let checksumValid: boolean | undefined;
  let checksumExpected: string | undefined;
  if (clean.length === 15) {
    const cs = validateGSTINChecksum(clean);
    checksumValid = cs.valid;
    checksumExpected = cs.expected;
  }

  let entityType: string | undefined;
  let entityTypeLabel: string | undefined;
  if (pan && pan.length >= 4) {
    entityType = pan.charAt(3);
    entityTypeLabel = PAN_ENTITY_TYPES[entityType] || "General Entity";
  }

  return {
    valid: clean.length === 15 && validateGSTIN(clean, true),
    stateCode,
    state,
    pan: pan && pan.length === 10 ? pan : undefined,
    entityNumber,
    checksum,
    checksumExpected,
    checksumValid,
    entityType,
    entityTypeLabel,
  };
}

export interface GSTINWarning {
  severity: "info" | "warning" | "error";
  message: string;
  suggestion?: string;
}

export function crossCheckGSTIN(
  gstin: string,
  enteredPAN?: string,
  selectedState?: string,
  selectedConstitution?: string
): GSTINWarning[] {
  const warnings: GSTINWarning[] = [];
  const clean = (gstin || "").trim().toUpperCase();
  if (!clean || clean.length < 2) return warnings;

  const stateCode = clean.slice(0, 2);
  const state = getStateByCode(stateCode);
  if (!state) {
    warnings.push({
      severity: "error",
      message: `Invalid State Code '${stateCode}' in GSTIN`,
      suggestion: "Indian GSTINs must begin with an official 2-digit state code (01-38).",
    });
  } else if (selectedState) {
    const expected = getStateByName(selectedState);
    if (expected && expected.code !== stateCode) {
      warnings.push({
        severity: "warning",
        message: `GSTIN belongs to ${state.name} (Code ${stateCode}), but registered business state is set to ${selectedState} (Code ${expected.code}).`,
        suggestion: `Click 'Update State to ${state.name}' to align them.`,
      });
    }
  }

  if (clean.length >= 12) {
    const extractedPan = clean.slice(2, 12);
    if (enteredPAN) {
      const cleanPan = enteredPAN.trim().toUpperCase();
      if (cleanPan && cleanPan.length === 10 && cleanPan !== extractedPan) {
        warnings.push({
          severity: "error",
          message: `GSTIN PAN (${extractedPan}) does not match entered PAN (${cleanPan}).`,
          suggestion: "Under Indian GST law, characters 3 to 12 must strictly match the legal entity PAN.",
        });
      }
    }

    if (selectedConstitution) {
      const panChar = extractedPan.charAt(3);
      if (selectedConstitution.includes("Proprietor") && panChar !== "P") {
        warnings.push({
          severity: "warning",
          message: `Constitution is Sole Proprietorship, but 4th character of PAN is '${panChar}' (expected 'P').`,
          suggestion: "Please verify whether this GSTIN was issued to an individual proprietor or a firm/company.",
        });
      } else if (selectedConstitution.includes("Private Limited") && panChar !== "C") {
        warnings.push({
          severity: "warning",
          message: `Constitution is Private Limited, but 4th character of PAN is '${panChar}' (expected 'C').`,
          suggestion: "Pvt Ltd companies typically hold a corporate PAN ending with 'C' as the 4th character.",
        });
      } else if ((selectedConstitution.includes("Partnership") || selectedConstitution.includes("LLP")) && panChar !== "F") {
        warnings.push({
          severity: "warning",
          message: `Constitution is Partnership/LLP, but 4th character of PAN is '${panChar}' (expected 'F').`,
        });
      }
    }
  }

  return warnings;
}
